import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const CollaborativeNotebook = ({ activeCase, onSave, collaborators }) => {
  const [content, setContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [conflictResolution, setConflictResolution] = useState(null);

  // Mock collaborative editing data
  const mockVersions = [
    {
      id: 'v1.0',
      timestamp: new Date(Date.now() - 3600000),
      author: 'Dr. Elena Rossi',
      changes: 'Initial claim analysis and source verification',
      status: 'published'
    },
    {
      id: 'v1.1',
      timestamp: new Date(Date.now() - 1800000),
      author: 'Marcus Weber',
      changes: 'Added forensic analysis results and metadata verification',
      status: 'draft'
    },
    {
      id: 'v1.2',
      timestamp: new Date(Date.now() - 900000),
      author: 'You',
      changes: 'Updated confidence scoring and evidence links',
      status: 'current'
    }
  ];

  const mockConflicts = [
    {
      id: 'conflict_1',
      section: 'Evidence Assessment',
      conflictType: 'concurrent_edit',
      users: ['Dr. Elena Rossi', 'Marcus Weber'],
      timestamp: new Date(Date.now() - 300000)
    }
  ];

  useEffect(() => {
    if (activeCase) {
      setContent(`# Fact-Check Investigation: ${activeCase?.title}

## Claim Analysis
**Original Claim:** "${activeCase?.claim}"
**Source:** ${activeCase?.source}
**Date Submitted:** ${new Date(activeCase.submittedAt)?.toLocaleDateString()}

## Methodology Applied
Following European Code of Standards for Fact-Checking:

### 1. Source Verification
- Primary source identification: ✓ Completed
- Source credibility assessment: ✓ Verified
- Cross-reference validation: ⏳ In Progress

### 2. Evidence Collection
- Documentary evidence: 3 sources attached
- Expert consultation: Dr. Maria Santos (Climate Science)
- Statistical verification: Pending review

### 3. Forensic Analysis
- Image metadata analysis: No manipulation detected
- Reverse image search: Original source confirmed
- Timestamp verification: Consistent with claim date

## Findings Summary
Based on comprehensive analysis of available evidence, the claim shows **moderate accuracy** with some contextual limitations.

**Confidence Level:** 75%
**Verdict Status:** Under Review
**Next Steps:** Awaiting peer review from secondary fact-checker`);
    }
  }, [activeCase]);

  const handleSave = () => {
    setLastSaved(new Date());
    onSave?.(content);
  };

  const handleConflictResolve = (conflictId, resolution) => {
    setConflictResolution({ conflictId, resolution });
    // Simulate conflict resolution
    setTimeout(() => {
      setConflictResolution(null);
    }, 2000);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="FileText" size={20} className="text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">Research Notebook</h3>
            <p className="text-sm text-muted-foreground">
              Collaborative editing • {collaborators?.length} active users
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Real-time Collaborators */}
          <div className="flex -space-x-2">
            {collaborators?.slice(0, 3)?.map((collaborator, index) => (
              <div
                key={index}
                className="w-8 h-8 bg-primary rounded-full border-2 border-card flex items-center justify-center text-xs font-medium text-primary-foreground"
                title={collaborator?.name}
              >
                {collaborator?.name?.charAt(0)}
              </div>
            ))}
            {collaborators?.length > 3 && (
              <div className="w-8 h-8 bg-muted rounded-full border-2 border-card flex items-center justify-center text-xs font-medium text-muted-foreground">
                +{collaborators?.length - 3}
              </div>
            )}
          </div>

          {/* Save Status */}
          <div className="flex items-center space-x-2 text-sm">
            {lastSaved ? (
              <span className="text-success">
                Saved {lastSaved?.toLocaleTimeString()}
              </span>
            ) : (
              <span className="text-warning">Unsaved changes</span>
            )}
            <Button
              variant="outline"
              size="sm"
              iconName="Save"
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
      {/* Conflict Resolution Banner */}
      {mockConflicts?.length > 0 && (
        <div className="bg-warning/10 border-b border-warning/20 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              <span className="text-sm font-medium text-warning">
                Editing conflict detected in "{mockConflicts?.[0]?.section}"
              </span>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="xs"
                onClick={() => handleConflictResolve(mockConflicts?.[0]?.id, 'accept_theirs')}
              >
                Accept Their Changes
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => handleConflictResolve(mockConflicts?.[0]?.id, 'accept_mine')}
              >
                Keep My Changes
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Editor Area */}
      <div className="flex-1 flex">
        {/* Main Editor */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30">
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                iconName="Bold"
                className="w-8 h-8"
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Italic"
                className="w-8 h-8"
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Link"
                className="w-8 h-8"
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Image"
                className="w-8 h-8"
              />
              <div className="w-px h-6 bg-border mx-2" />
              <Button
                variant="ghost"
                size="sm"
                iconName="Undo"
                className="w-8 h-8"
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Redo"
                className="w-8 h-8"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-xs text-muted-foreground">
                {content?.length} characters
              </span>
              <Button
                variant="ghost"
                size="sm"
                iconName="Eye"
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Preview' : 'Edit'}
              </Button>
            </div>
          </div>

          <div className="flex-1 p-4">
            {isEditing ? (
              <div className="prose prose-sm max-w-none">
                <div dangerouslySetInnerHTML={{ __html: content?.replace(/\n/g, '<br>') }} />
              </div>
            ) : (
              <textarea
                value={content}
                onChange={(e) => setContent(e?.target?.value)}
                className="w-full h-full resize-none border-none outline-none bg-transparent text-sm font-mono leading-relaxed"
                placeholder="Start documenting your fact-checking process..."
              />
            )}
          </div>
        </div>

        {/* Version History Sidebar */}
        <div className="w-80 border-l border-border bg-muted/20">
          <div className="p-4 border-b border-border">
            <h4 className="font-medium text-foreground mb-2">Version History</h4>
            <p className="text-xs text-muted-foreground">
              Track changes and collaborate with team members
            </p>
          </div>

          <div className="p-4 space-y-3">
            {mockVersions?.map((version) => (
              <div
                key={version?.id}
                className="p-3 bg-card rounded-lg border border-border hover:shadow-soft transition-smooth cursor-pointer"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">
                    {version?.id}
                  </span>
                  <div className={`
                    px-2 py-1 rounded-full text-xs font-medium
                    ${version?.status === 'current' ? 'bg-success/10 text-success' :
                      version?.status === 'published'? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}
                  `}>
                    {version?.status}
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground mb-2">
                  {version?.changes}
                </p>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{version?.author}</span>
                  <span>{version?.timestamp?.toLocaleTimeString()}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              iconName="History"
              fullWidth
            >
              View Full History
            </Button>
          </div>
        </div>
      </div>
      {/* Footer Actions */}
      <div className="flex items-center justify-between p-4 border-t border-border bg-muted/20">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Clock" size={16} />
          <span>Auto-save enabled</span>
          <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
        </div>

        <div className="flex space-x-2">
          <Button
            variant="outline"
            iconName="MessageSquare"
            onClick={() => {
              // Open comments panel
            }}
          >
            Comments (3)
          </Button>
          <Button
            variant="outline"
            iconName="Share"
            onClick={() => {
              // Share notebook
            }}
          >
            Share
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CollaborativeNotebook;